import { cloneDeep } from "@apollo/client/utilities"
import { Box } from "@mui/material"
import { Bound } from "lib/@components/binding/Bound"
import { Else } from "lib/@components/else"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useMeasurement } from "lib/@hooks/useMeasurement"
import { ComparisonUpdated } from "event-definitions"
import { idNotEqual } from "lib/idEquals"
import { getTaskAmendsKey } from "authoring/routes/project/amends/lib/required-amends-helpers"
import { InlineCompareButtons } from "routes/schedule/compare/inline-approval-buttons/inline-compare-buttons"
import { TwoSchedules } from "routes/schedule/compare/twoSchedules"
import { TaskOrHeader } from "routes/schedule/tasks/taskOrHeader"
import { If } from "lib/@components/switch"
import { AuthoringReviewAmendsBlock } from "./authoring-review-amends-block"
import { AuthoringReviewPriorRejection } from "./authoring-review-prior-rejection"
import { findAuthoringTask, normalizeAuthoringTask } from "./lib/authoring-task-lookup"

export function AuthoringReviewMissingTask({ task }) {
    const [{ width, height }, attach] = useMeasurement()
    const { comparisonTarget, target } = useBoundContext()
    const editedTask = findAuthoringTask(target.tasks, task)
    const taskAmendsKey = getTaskAmendsKey(task)

    return (
        <Bound acceptId={task.title}>
            <TwoSchedules
                key={task.id}
                copy={copyTask(task)}
                copied={editedTask}
                revert={revertTask(task)}
                showCopy={true}
                leftSchedule={target}
                disabled={!editedTask}
                rightSchedule={comparisonTarget}
                left={
                    <Bound>
                        <Box width={1}>
                            <If value={editedTask}>
                                <TaskOrHeader last={true} item={editedTask} />
                                <Else>
                                    <Box py={1}>
                                        <Box
                                            borderRadius={1}
                                            width={width}
                                            p={1}
                                            height={height}
                                            minHeight={80}
                                            minWidth={180}
                                            textAlign="center"
                                            alignItems="center"
                                            bgcolor="action.selected"
                                            display="flex"
                                            justifyContent="center"
                                        >
                                            Not found in this version
                                        </Box>
                                    </Box>
                                </Else>
                            </If>
                        </Box>
                    </Bound>
                }
                right={
                    <Bound>
                        <Box ref={attach}>
                            <TaskOrHeader last={true} item={task} />
                        </Box>
                    </Bound>
                }
                after={
                    <InlineCompareButtons
                        idFromGroup={task.title}
                        to={normalizeAuthoringTask(task)}
                        from={editedTask}
                    />
                }
                below={
                    <>
                        <AuthoringReviewPriorRejection amendsKey={taskAmendsKey} />
                        <AuthoringReviewAmendsBlock
                            amendsKey={taskAmendsKey}
                            subtitle="Change Task Reason"
                            cyPrefix="side-by-side-amends"
                        />
                    </>
                }
            />
        </Bound>
    )

    function copyTask(taskToCopy) {
        return () => {
            target.tasks.push(cloneDeep(taskToCopy))
            ComparisonUpdated.raiseOnce()
        }
    }

    function revertTask(taskToRevert) {
        return () => {
            target.tasks = target.tasks.filter(idNotEqual(taskToRevert.id))
            ComparisonUpdated.raiseOnce()
        }
    }
}
